import Config from './config'

class Api extends Config {
  ping = async () => {
    return await this.request('ping', 'GET', '')
  }

  login = async data => {
    return await this.request('login', 'POST', data)
  }

  refresh = async data => {
    return await this.request('refresh', 'POST', data)
  }

  companyRegistration = async data => {
    return await this.request('company/new', 'POST', data)
  }

  registration = async data => {
    return await this.request('users/new', 'POST', data)
  }

  user = async uid => {
    const params = new URLSearchParams({
      uid: uid,
    })
    return await this.request('users', 'GET', params)
  }

  userUpdate = async userData => {
    return await this.request('users/update', 'POST', userData)
  }

  userDelete = async uid => {
    const params = new URLSearchParams({
      uid: uid,
    })
    return await this.request('users/delete', 'GET', params)
  }

  getExchanges = async () => {
    return await this.request('exchanges', 'GET', '')
  }

  getCompanyStrategies = async cid => {
    const params = new URLSearchParams({
      cid: cid,
    })
    return await this.request('company/strategies', 'GET', params)
  }

  getUserStrategies = async (uid, sid) => {
    const params = new URLSearchParams({
      uid: uid,
      sid: sid,
    })
    return await this.request('users/strategy', 'GET', params)
  }

  getStrategyInfo = async (id, num) => {
    const params = new URLSearchParams({
      id: id,
      num: num,
    })
    return await this.request('strategies/info', 'GET', params)
  }

  getStrategiesTypes = async cid => {
    const params = new URLSearchParams({
      cid: cid,
    })
    return await this.request('strategies/types', 'GET', params)
  }

  getStrategiesInfo = async id => {
    const params = new URLSearchParams({
      id: id,
    })
    return await this.request('strategies/config_info', 'GET', params)
  }

  getBalance = async id => {
    const params = new URLSearchParams({
      id: id,
    })
    return await this.request('bot/balance', 'GET', params)
  }

  getBalancesFull = async botId => {
    const params = new URLSearchParams({
      bot_id: botId,
    })
    return await this.request('bot/balances_full', 'GET', params, false, true)
  }

  getBalancesAll = async cid => {
    const params = new URLSearchParams({
      cid,
    })
    return await this.request('bot/balances_all', 'GET', params)
  }

  createStrategy = async strategyData => {
    return await this.request('strategies/new', 'POST', strategyData)
  }

  updateStrategy = async strategyData => {
    // failSilently: форма показывает свой инлайн-статус «Применяется… / Ошибка + Повторить»,
    // поэтому глобальный тост об ошибке POST (например, 409 «уже применяется») не нужен.
    // На успех BFF возвращает { success: true, reload: 'applying' }.
    return await this.request('strategies/update', 'POST', strategyData, false, true)
  }

  // GET /strategies/reload_status?id=<sid>
  // -> { status: 'applying' | 'done' | 'failed', message?: string, updated: string }
  // Поллится после updateStrategy, чтобы узнать исход асинхронного сохранения + reload бота.
  getStrategyReloadStatus = async sid => {
    const params = new URLSearchParams({
      id: sid,
    })
    // failSilently: временные ошибки сети при поллинге не должны спамить глобальный алерт.
    return await this.request('strategies/reload_status', 'GET', params, false, true)
  }

  deleteStrategy = async id => {
    const params = new URLSearchParams({
      id: id,
    })
    return await this.request('strategies/delete', 'GET', params)
  }

  toggleStrategy = async (id, toggleType) => {
    const params = new URLSearchParams({
      id: id,
    })
    if (toggleType) {
      return await this.request('strategies/start', 'GET', params)
    } else {
      return await this.request('strategies/stop', 'GET', params)
    }
  }


  getPairs = async exch => {
    const params = new URLSearchParams({
      exch: exch,
    })
    return await this.request('pairs', 'GET', params)
  }


  getCompanyUsers = async cid => {
    const params = new URLSearchParams({
      cid: cid,
    })
    return await this.request('company/users', 'GET', params)
  }

  getCompanyKeys = async cid => {
    const params = new URLSearchParams({
      cid: cid,
    })
    return await this.request('company/keys_sets', 'GET', params)
  }


  createApiKey = async data => {
    return await this.request('keys_sets/new', 'POST', data)
  }

  updateApiKey = async data => {
    return await this.request('keys_sets/update', 'POST', data)
  }


  disableApiKey = async key => {
    const params = new URLSearchParams({
      id: key.id,
      todo: key.active ? 'stop' : 'start',
    })
    return await this.request('bots', 'GET', params)
  }

  deleteApiKey = async key => {
    const params = new URLSearchParams({
      id: key.id,
    })
    return await this.request('bots/delete', 'GET', params)
  }

  getBotTokenAddresses = async bot_id => {
    const params = new URLSearchParams({
      bot_id: bot_id,
    })
    return await this.request('bot/token_addresses', 'GET', params)
  }

  createBotTokenAddress = async data => {
    return await this.request('bot/token_address/add', 'POST', data, true)
  }

  updateBotTokenAddress = async data => {
    return await this.request('bot/token_address/update', 'POST', data, true)
  }

  deleteBotTokenAddress = async id => {
    return await this.request('bot/token_address/delete?id=' + id, 'DELETE', null, true)
  }


  /**
   * Получает ордеры для бота
   *
   * @param {number} bot_id
   *
   * @returns {Promise}
   *
   * @async
   * @method
   * @memberof Api
   */
  getBotOrders = async bot_id => {
    const params = new URLSearchParams({
      bot_id: bot_id,
    })
    return await this.request('bot/orders', 'GET', params)
  }

  /**
   * Отменяет ордеры
   *
   * @param {Object} params
   * @param {number} params.bot_id
   * @param {Array} params.orders
   *
   * @returns {Promise}
   *
   * @async
   * @method
   * @memberof Api
   */
  cancelOrders = async params => {
    return await this.request('bot/orders/cancel', 'POST', params)
  }


  createDeal = async params => {
    return await this.request('bot/deal', 'POST', params)
  }

  updateDealStatus = async id => {
    const params = new URLSearchParams({
      id: id,
    })

    return await this.request('bot/deal/status', 'GET', params)
  }

  cancelDeal = async id => {
    const params = new URLSearchParams({
      id: id,
    })

    return await this.request('bot/deal/cancel', 'GET', params)
  }

  initBalancesAllWS = cid => {
    const jwt = this.getStoredJwt()
    const isDev = process.env.NODE_ENV === 'development'
    const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://'
    const wsHost = isDev
      ? this.fullUrl.replace(/^https?:\/\//, '')
      : window.location.host
    const url = `${wsProtocol}${wsHost}${isDev ? '' : '/api'}`

    try {
      return new WebSocket(`${url}/bot/balances_ws?cid=${cid}&token=${jwt?.jwt}`)
    }
    catch (err) {
      throw new Error(err)
    }
  }

  initBalanceWS = (cid, bot_id) => {
    const jwt = this.getStoredJwt()
    const isDev = process.env.NODE_ENV === 'development'
    const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://'
    const wsHost = isDev
      ? this.fullUrl.replace(/^https?:\/\//, '')
      : window.location.host
    const url = `${wsProtocol}${wsHost}${isDev ? '' : '/api'}`

    try {
      return new WebSocket(`${url}/bot/balance_ws?cid=${cid}&bot_id=${bot_id}&token=${jwt?.jwt}`)
    }
    catch (err) {
      throw new Error(err)
    }
  }
}

export default new Api()
